import { FormEvent, useEffect, useMemo, useState } from "react";

import { updateDeal, UpdateDealRequest } from "../../service/fund";
import { ServiceError, Status } from "../../service/Shared";
import { convertDateToStringYYYYMMDD, getDealDateValidationErrorFieldKeys, useIsLoadingWrapper } from "../../utils";
import { Toast } from "../../widget";
import { DealStageDateSection, DealStageDatesRecord, EditDealDatesPanelProps } from "./models/EditDealDatesPanel";
import { mapDealDatesToSections, parseDealDates, updateDealDateSection } from "./utils/dealDateStageUtils";

interface UseEditDealDatesPanelReturnData {
  errors: ServiceError[] | undefined;
  errorFieldKeys: Set<string>;
  isSaving: boolean;
  sections: DealStageDateSection[];
  handleClose: () => void;
  handleDateChange: (stageKey: string, field: string, value: Date | undefined) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}

export const useEditDealDatesPanel = ({
  deal,
  workflowTemplatePhases = [],
  show,
  onClose,
  onSaved,
}: EditDealDatesPanelProps): UseEditDealDatesPanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [editableDealDates, setEditableDealDates] = useState<DealStageDatesRecord>(parseDealDates(deal?.dealDates));

  useEffect(() => {
    if (!show) {
      return;
    }

    setErrors(undefined);
    setEditableDealDates(parseDealDates(deal?.dealDates));
  }, [deal, show]);

  const sections = useMemo(
    () => mapDealDatesToSections(editableDealDates, workflowTemplatePhases),
    [editableDealDates, workflowTemplatePhases]
  );
  const errorFieldKeys = useMemo(() => getDealDateValidationErrorFieldKeys(errors), [errors]);

  const handleClose = (): void => {
    setErrors(undefined);
    setEditableDealDates(parseDealDates(deal?.dealDates));
    onClose();
  };

  const handleDateChange = (stageKey: string, field: string, value: Date | undefined): void => {
    setEditableDealDates((previousDealDates) =>
      updateDealDateSection(
        previousDealDates,
        stageKey,
        field,
        convertDateToStringYYYYMMDD(value),
        workflowTemplatePhases
      )
    );
  };

  const handleSubmit = useIsLoadingWrapper(async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setErrors(undefined);

    if (!deal) {
      setErrors([{ code: "DEAL_NOT_FOUND", message: "Unable to determine which deal to update." }]);
      return;
    }

    const request: UpdateDealRequest = {
      conversionProbability: deal.conversionProbability,
      dealDates: editableDealDates as unknown as UpdateDealRequest["dealDates"],
      dealSpecificData: deal.dealSpecificData as unknown as UpdateDealRequest["dealSpecificData"],
      dealTeam: deal.usersInvolved?.map((user) => user.uuid) ?? [],
      dealUuid: deal.uuid,
      description: deal.description ?? "",
      estimatedFees: deal.estimatedFees ?? 0,
      introductionDate: deal.introductionDate ?? null,
      IsTemplateSelected: undefined,
      leadUserUuid: deal.leadUser?.uuid ?? "",
      name: deal.name,
      projectedDpi: deal.projectedDpi ?? null,
      relatedObjects:
        deal.relatedObjects?.map((relatedObject) => ({
          objectType: relatedObject.objectType,
          objectUuid: relatedObject.objectUuid,
        })) ?? [],
      rowVersion: deal.rowVersion,
      source: deal.source ?? null,
      stage: deal.stage,
      status: deal.status,
      targetCloseDate: deal.targetCloseDate ?? null,
    };

    const updateDealRes = await updateDeal(request);
    if (updateDealRes.status === Status.Error) {
      setErrors(updateDealRes.errors);
      return;
    }

    Toast.success({ message: "Deal stage dates successfully updated" });
    if (onSaved) {
      onSaved();
      return;
    }

    onClose();
  }, setIsSaving);

  return {
    errors,
    errorFieldKeys,
    isSaving,
    sections,
    handleClose,
    handleDateChange,
    handleSubmit,
  };
};
